import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function LimitingReactantCalculator() {
  const [reactant1Moles, setReactant1Moles] = useState("");
  const [reactant1Coeff, setReactant1Coeff] = useState("");
  const [reactant2Moles, setReactant2Moles] = useState("");
  const [reactant2Coeff, setReactant2Coeff] = useState("");
  const [result, setResult] = useState<string | null>(null);

  const calculate = () => {
    const r1m = parseFloat(reactant1Moles);
    const r1c = parseFloat(reactant1Coeff);
    const r2m = parseFloat(reactant2Moles);
    const r2c = parseFloat(reactant2Coeff);

    if (isNaN(r1m) || isNaN(r1c) || isNaN(r2m) || isNaN(r2c) || r1c <= 0 || r2c <= 0 || r1m < 0 || r2m < 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const ratio1 = r1m / r1c;
    const ratio2 = r2m / r2c;

    const limiting = ratio1 < ratio2 ? "Reactant 1" : "Reactant 2";
    setResult(limiting);

    toast({
      title: "Calculation Complete",
      description: `Limiting Reactant: ${limiting}`,
    });
  };

  const reset = () => {
    setReactant1Moles("");
    setReactant1Coeff("");
    setReactant2Moles("");
    setReactant2Coeff("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Limiting Reactant Calculator" 
      calculatorPath="/calculator/limiting-reactant"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Limiting Reactant Calculator</CardTitle>
            <CardDescription>
              Determine which reactant limits the reaction
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="r1moles">Reactant 1 Moles</Label>
                  <Input
                    id="r1moles"
                    type="number"
                    step="any"
                    value={reactant1Moles}
                    onChange={(e) => setReactant1Moles(e.target.value)}
                    placeholder="Moles"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r1coeff">Reactant 1 Coefficient</Label>
                  <Input
                    id="r1coeff"
                    type="number"
                    step="any"
                    value={reactant1Coeff}
                    onChange={(e) => setReactant1Coeff(e.target.value)}
                    placeholder="Coefficient"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="r2moles">Reactant 2 Moles</Label>
                  <Input
                    id="r2moles"
                    type="number"
                    step="any"
                    value={reactant2Moles}
                    onChange={(e) => setReactant2Moles(e.target.value)}
                    placeholder="Moles"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="r2coeff">Reactant 2 Coefficient</Label>
                  <Input
                    id="r2coeff"
                    type="number"
                    step="any"
                    value={reactant2Coeff}
                    onChange={(e) => setReactant2Coeff(e.target.value)}
                    placeholder="Coefficient"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Limiting Reactant</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{result}</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">How It Works</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  The limiting reactant is determined by comparing the mole ratio of each reactant 
                  to its stoichiometric coefficient. The reactant with the smallest ratio is limiting.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
